import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (_e) {
      setUser(false);
      return null;
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("access_token");

    if (token) {
      refreshMe();
    } else {
      setUser(false);
      setReady(true);
    }
  }, [refreshMe]);

  const login = useCallback(
    async ({ email, password, role }) => {
      try {
        const { data } = await api.post("/auth/login", {
          email,
          password,
          role,
        });

        // Save the JWT returned by the backend.
        if (data.access_token) {
          localStorage.setItem("access_token", data.access_token);
        } else {
          return {
            ok: false,
            error: "Login succeeded but no access token was returned.",
          };
        }

        // Use the successful login response immediately.
        setUser(data);
        setReady(true);

        return {
          ok: true,
          user: data,
        };
      } catch (e) {
        return {
          ok: false,
          error: formatApiError(
            e.response?.data?.detail,
            "Login failed"
          ),
        };
      }
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      // Ignore logout errors.
    }

    localStorage.removeItem("access_token");
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        ready,
        login,
        logout,
        refreshMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return ctx;
}
